import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const _BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function _getApiBase() {
  try {
    const backendHost = new URL(_BACKEND_URL).hostname;
    if (typeof window !== 'undefined' && window.location.hostname !== backendHost) {
      return '/api';
    }
  } catch {}
  return `${_BACKEND_URL}/api`;
}

const API_BASE = _getApiBase();

function formatApiError(detail, status) {
  if (detail == null) {
    if (status === 429) return "Troppi tentativi. Riprova tra qualche minuto.";
    if (status === 401) return "Email o password non validi.";
    if (status === 403) return "Accesso non autorizzato.";
    if (status >= 500) return `Errore server (${status}). Riprova tra poco.`;
    if (status) return `Errore di rete (HTTP ${status}). Riprova.`;
    return "Errore di connessione. Verifica la rete e riprova.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

/**
 * Safe fetch wrapper — catches platform-injected postMessage errors
 * and falls back to XMLHttpRequest.
 */
async function safeFetch(url, options = {}) {
  try {
    return await fetch(url, options);
  } catch (err) {
    if (err && err.message && err.message.includes('postMessage')) {
      // Platform script interference — fallback to XHR
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(options.method || 'GET', url, true);
        xhr.withCredentials = true;
        if (options.headers) {
          Object.entries(options.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        }
        xhr.onload = () => {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            json: () => Promise.resolve(JSON.parse(xhr.responseText)),
          });
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(options.body || null);
      });
    }
    throw err;
  }
}

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const res = await safeFetch(`${API_BASE}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        setUser(false);
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    let res;
    try {
      res = await safeFetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
    } catch (err) {
      // Network-level failure (DNS / TLS / connection reset).
      throw new Error("Errore di connessione. Verifica la rete e riprova.");
    }
    if (!res.ok) {
      let data = {};
      try { data = await res.json(); } catch { /* HTML/empty body */ }
      throw new Error(formatApiError(data.detail, res.status));
    }
    const data = await res.json();
    setUser(data);
    return data;
  };

  const logout = async () => {
    await safeFetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
